// AutomationContext — single point of entry for firing on-enter automation
// rules from any stage-change site (Pipeline kanban, Pipeline edit panel,
// Clients job/ticket panels). Owns the slide-up confirmation prompt queue and
// the toast queue rendered by AutomationOverlay.

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import {
  createAutomationEvent,
  createAutomationTask,
  markAutomationEventDismissed,
} from '../services/automationService';
import { normalizeRule } from '../utils/automationUtils';
import { findJobType } from '../utils/jobTypesUtils';

const AutomationContext = createContext(null);

export function useAutomation() {
  const ctx = useContext(AutomationContext);
  if (!ctx) {
    // The provider may not be mounted in tests / isolated renders. Return
    // no-op functions so call sites don't crash.
    return {
      triggerOnEnterAutomations: async () => {},
      pendingPrompts: [],
      toasts: [],
      confirmPrompt: () => {},
      dismissPrompt: () => {},
      removeToast: () => {},
    };
  }
  return ctx;
}

export function AutomationProvider({ children }) {
  const [pendingPrompts, setPendingPrompts] = useState([]);
  const [toasts, setToasts] = useState([]);
  const toastSeqRef = useRef(0);

  const showToast = useCallback((message) => {
    toastSeqRef.current += 1;
    const id = `t_${toastSeqRef.current}_${Date.now()}`;
    setToasts(prev => [...prev, { id, message }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4000);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Look up the active rules (onEnter, active) for the new stage on the job's
  // type. Returns an array of canonicalized rule objects.
  const findOnEnterRules = useCallback((job, newStage, jobTypes) => {
    const t = findJobType(jobTypes, job.jobTypeId) || findJobType(jobTypes, job.jobType);
    const stage = (t?.defaultStages || []).find(s => s.name === newStage);
    if (!stage || !Array.isArray(stage.automationRules)) return [];
    return stage.automationRules
      .map(normalizeRule)
      .filter(r => r && r.active && r.triggerType === 'onEnter');
  }, []);

  // Resolve display names for a list of uids using companyUsers.
  const resolveNames = useCallback((uids, companyUsers) => {
    return uids.map(uid => {
      const u = (companyUsers || []).find(u => u.uid === uid);
      if (!u) return '';
      return (u.firstName && u.lastName) ? `${u.firstName} ${u.lastName}`.trim() : (u.email || '');
    });
  }, []);

  // Fire onEnter rules for a job that just moved to newStage. Auto-run rules
  // create their tasks immediately and surface a toast; require-confirmation
  // rules push a prompt onto the queue for the user to confirm or dismiss.
  const triggerOnEnterAutomations = useCallback(async ({
    job,
    newStage,
    jobTypes,
    userProfile,
    companyUsers,
  }) => {
    if (!job?.id || !job?.companyId || !newStage) return;
    const rules = findOnEnterRules(job, newStage, jobTypes);
    if (rules.length === 0) return;

    const triggeredByUid = userProfile?.uid || null;
    const triggeredByName = userProfile && (userProfile.firstName || userProfile.lastName)
      ? `${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim()
      : (userProfile?.email || '');

    for (const rule of rules) {
      if (rule.confirmationMode === 'autoRun') {
        // Auto-run: pre-resolve assignees, create event + task, toast.
        const assigneeIds = rule.assigneeType === 'noDefault'
          ? (triggeredByUid ? [triggeredByUid] : [])
          : [...rule.defaultAssignees];
        const assigneeNames = resolveNames(assigneeIds, companyUsers);
        try {
          const eventId = await createAutomationEvent({
            companyId: job.companyId,
            jobId: job.id,
            ruleId: rule.id,
            stageAtTrigger: newStage,
            status: 'pending',
            triggeredAt: new Date(),
          });
          const taskId = await createAutomationTask({
            companyId: job.companyId,
            jobId: job.id,
            jobName: job.jobName || job.clientName || '',
            ruleId: rule.id,
            eventId,
            taskType: rule.taskType,
            description: rule.description,
            assignedToIds: assigneeIds,
            assignedToNames: assigneeNames,
            emailNotification: rule.emailNotification,
            triggeredByUid,
            triggeredByName,
          });
          // Patch the event with the created taskId and mark as 'pushed' (i.e.
          // the task is now live in the system — Cloud Functions can ignore).
          const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
          const { db } = await import('../firebase');
          await updateDoc(doc(db, 'automationEvents', eventId), {
            taskId,
            status: 'pushed',
            updatedAt: serverTimestamp(),
          });
          const peopleLabel = assigneeNames.filter(Boolean).join(', ') || 'team';
          showToast(`Automation triggered — ${rule.description || 'task'} assigned to ${peopleLabel}`);
        } catch (err) {
          console.error('Auto-run automation failed:', err);
          showToast('Automation failed — check console');
        }
      } else {
        // Require confirmation: push to prompt queue.
        const defaultIds = rule.assigneeType === 'noDefault' ? [] : [...rule.defaultAssignees];
        setPendingPrompts(prev => [
          ...prev,
          {
            id: `pp_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
            rule,
            job,
            newStage,
            jobTypes,
            companyUsers,
            triggeredByUid,
            triggeredByName,
            defaultIds,
            sendEmail: !!rule.emailNotification,
          },
        ]);
      }
    }
  }, [findOnEnterRules, resolveNames, showToast]);

  // Confirm a queued prompt — create event + task with the user's choices.
  const confirmPrompt = useCallback(async (promptId, { assigneeIds, sendEmail }) => {
    const prompt = pendingPrompts.find(p => p.id === promptId);
    if (!prompt) return;
    const { rule, job, newStage, companyUsers, triggeredByUid, triggeredByName } = prompt;
    console.log('confirmPrompt — job:', job, 'rule:', rule);
    const ids = Array.isArray(assigneeIds) ? assigneeIds.filter(Boolean) : [];
    const names = ids.map(uid => {
      const u = (companyUsers || []).find(u => u.uid === uid);
      if (!u) return '';
      return (u.firstName && u.lastName) ? `${u.firstName} ${u.lastName}`.trim() : (u.email || '');
    });
    try {
      let eventId;
      try {
        eventId = await createAutomationEvent({
          companyId: job.companyId,
          jobId: job.id,
          ruleId: rule.id,
          stageAtTrigger: newStage,
          status: 'pending',
          triggeredAt: new Date(),
        });
      } catch (err) {
        console.error('createAutomationEvent failed:', err);
        throw err;
      }
      let taskId;
      try {
        taskId = await createAutomationTask({
          companyId: job.companyId,
          jobId: job.id,
          jobName: job.jobName || job.clientName || '',
          ruleId: rule.id,
          eventId,
          taskType: rule.taskType,
          description: rule.description,
          assignedToIds: ids,
          assignedToNames: names,
          emailNotification: !!sendEmail,
          triggeredByUid,
          triggeredByName,
        });
      } catch (err) {
        console.error('createAutomationTask failed:', err);
        throw err;
      }
      const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
      const { db } = await import('../firebase');
      await updateDoc(doc(db, 'automationEvents', eventId), {
        taskId,
        status: 'active',
        updatedAt: serverTimestamp(),
      });
      showToast(`Task created — ${rule.description || 'automation task'}`);
    } catch (err) {
      console.error('Automation confirm failed:', err);
      showToast('Automation failed — check console');
    }
    setPendingPrompts(prev => prev.filter(p => p.id !== promptId));
  }, [pendingPrompts, showToast]);

  // Dismiss a queued prompt — log a dismissed event but do NOT create a task.
  const dismissPrompt = useCallback(async (promptId) => {
    const prompt = pendingPrompts.find(p => p.id === promptId);
    if (!prompt) {
      setPendingPrompts(prev => prev.filter(p => p.id !== promptId));
      return;
    }
    const { rule, job, newStage } = prompt;
    try {
      const eventId = await createAutomationEvent({
        companyId: job.companyId,
        jobId: job.id,
        ruleId: rule.id,
        stageAtTrigger: newStage,
        status: 'dismissed',
        triggeredAt: new Date(),
      });
      await markAutomationEventDismissed(eventId);
    } catch (err) {
      console.error('Automation dismiss logging failed:', err);
    }
    setPendingPrompts(prev => prev.filter(p => p.id !== promptId));
  }, [pendingPrompts]);

  const value = {
    triggerOnEnterAutomations,
    pendingPrompts,
    toasts,
    confirmPrompt,
    dismissPrompt,
    removeToast,
  };

  return (
    <AutomationContext.Provider value={value}>
      {children}
    </AutomationContext.Provider>
  );
}
